import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "./ui/button"

const questions = [
  {
    id: "texture",
    text: "Pick your favorite texture",
    options: [
      { id: "wood", image: "/Quiz_Natural Wood.png", text: "Natural Wood", aesthetic: "scandinavian" },
      { id: "velvet", image: "/Quiz_Plush Velvet.png", text: "Plush Velvet", aesthetic: "luxe" },
      { id: "linen", image: "/Quiz_Soft Linen.png", text: "Soft Linen", aesthetic: "coastal" },
      { id: "marble", image: "/Quiz_Veined Marble.png", text: "Veined Marble", aesthetic: "modern" },
    ],
  },
  {
    id: "weekend",
    text: "How do you spend weekends?",
    options: [
      {
        id: "hiking",
        image: "/Quiz_Outdoor Adventure.png",
        text: "Outdoor Adventure",
        aesthetic: "scandinavian",
      },
      {
        id: "reading",
        image: "/Quiz_Reading by the Window.png",
        text: "Reading by the Window",
        aesthetic: "coastal",
      },
      { id: "hosting", image: "/Quiz_Hosting Friends.png", text: "Hosting Friends", aesthetic: "luxe" },
      { id: "museum", image: "/Quiz_Museum & Galleries.png", text: "Museum & Galleries", aesthetic: "modern" },
    ],
  },
  {
    id: "color",
    text: "Which color palette speaks to you?",
    options: [
      {
        id: "neutrals",
        image: "/Quiz_Warm Neutrals.png",
        text: "Warm Neutrals",
        aesthetic: "scandinavian",
      },
      { id: "blues", image: "/Quiz_Ocean Blues.png", text: "Ocean Blues", aesthetic: "coastal" },
      { id: "rich", image: "/Quiz_Rich Jewel Tones.png", text: "Rich Jewel Tones", aesthetic: "luxe" },
      { id: "monochrome", image: "/Quiz_Monochrome.png", text: "Monochrome", aesthetic: "modern" },
    ],
  },
  {
    id: "space",
    text: "What makes a space feel like home?",
    options: [
      {
        id: "comfort",
        image: "/Quiz_Comfort & Warm.png",
        text: "Comfort & Warmth",
        aesthetic: "scandinavian",
      },
      { id: "light", image: "/Quiz_Natural Light.png", text: "Natural Light", aesthetic: "coastal" },
      { id: "elegance", image: "/Quiz_Elegance & Detail.png", text: "Elegance & Detail", aesthetic: "luxe" },
      { id: "minimalism", image: "/Quiz_Clean Lines.png", text: "Clean Lines", aesthetic: "modern" },
    ],
  },
]

const aesthetics = {
  scandinavian: {
    name: "Nordic Minimalist",
    description: "Your style embraces natural materials, clean lines, and functional comfort with a warm touch.",
    image: "/placeholder.svg?height=600&width=800",
    collection: "Fjord Collection",
  },
  coastal: {
    name: "Coastal Serenity",
    description: "Your style celebrates light, air, and the tranquility of seaside living with soft textures.",
    image: "/placeholder.svg?height=600&width=800",
    collection: "Shoreline Collection",
  },
  luxe: {
    name: "Timeless Luxury",
    description: "Your style favors rich textures, elegant details, and statement pieces that exude sophistication.",
    image: "/placeholder.svg?height=600&width=800",
    collection: "Heritage Collection",
  },
  modern: {
    name: "Contemporary Edge",
    description: "Your style embraces bold contrasts, architectural forms, and innovative materials.",
    image: "/placeholder.svg?height=600&width=800",
    collection: "Metro Collection",
  },
}

export default function StyleCurator() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [result, setResult] = useState(null)
  const [quizComplete, setQuizComplete] = useState(false)

  const handleAnswer = (questionId, aesthetic) => {
    const newAnswers = { ...answers, [questionId]: aesthetic }
    setAnswers(newAnswers)

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    } else {
      // Calculate result
      const aestheticCounts = {}
      Object.values(newAnswers).forEach((aesthetic) => {
        aestheticCounts[aesthetic] = (aestheticCounts[aesthetic] || 0) + 1
      })

      let maxCount = 0
      let dominantAesthetic = ""

      Object.entries(aestheticCounts).forEach(([aesthetic, count]) => {
        if (count > maxCount) {
          maxCount = count
          dominantAesthetic = aesthetic
        }
      })

      setResult(dominantAesthetic)
      setQuizComplete(true)
    }
  }

  const resetQuiz = () => {
    setCurrentQuestionIndex(0)
    setAnswers({})
    setResult(null)
    setQuizComplete(false)
  }

  return (
    <section className="mobile-section-padding bg-white py-24">
      <div className="container mx-auto px-4">
        <motion.h2
          className="mobile-heading-xl mobile-element-spacing mb-4 text-center font-serif text-3xl font-light md:text-4xl"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Help Me Find My Aesthetic
        </motion.h2>

        <motion.p
          className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Discover your unique design style with our interactive quiz.
        </motion.p>

        <div className="mx-auto max-w-4xl">
          <AnimatePresence mode="wait">
            {!quizComplete ? (
              <motion.div
                key="question"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="mb-8 text-center font-serif text-2xl">{questions[currentQuestionIndex].text}</h3>

                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {questions[currentQuestionIndex].options.map((option) => (
                    <motion.div
                      key={option.id}
                      className="cursor-pointer"
                      whileHover={{ y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleAnswer(questions[currentQuestionIndex].id, option.aesthetic)}
                    >
                      <div className="relative mb-3 h-48 overflow-hidden md:h-64">
                        <img
                          src={option.image || "/placeholder.svg"}
                          alt={option.text}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <p className="text-center">{option.text}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 flex justify-center">
                  <div className="flex gap-1">
                    {questions.map((_, index) => (
                      <div
                        key={index}
                        className={`h-1 w-8 rounded-full ${
                          index === currentQuestionIndex
                            ? "bg-amber-800"
                            : index < currentQuestionIndex
                              ? "bg-amber-300"
                              : "bg-stone-200"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                {result && (
                  <>
                    <h3 className="mb-2 font-serif text-3xl font-light">Your Aesthetic: {aesthetics[result].name}</h3>
                    <p className="mx-auto mb-8 max-w-2xl text-muted-foreground">{aesthetics[result].description}</p>

                    <div className="mx-auto mb-8 aspect-video max-w-3xl overflow-hidden">
                      <img
                        src={aesthetics[result].image || "/placeholder.svg"}
                        alt={aesthetics[result].name}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <p className="mb-8 text-xl font-medium">Perfect Match: {aesthetics[result].collection}</p>

                    <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                      <Button size="lg" className="rounded-none px-8">
                        Show Me My Space
                      </Button>
                      <Button variant="outline" size="lg" className="rounded-none px-8" onClick={resetQuiz}>
                        Retake Quiz
                      </Button>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
} 